import { createSlice } from "@reduxjs/toolkit";
import { EmployeeInterface } from "../../common/interfaces/EmployeeInterface";

export interface EditEmployeeModalInterface {
    selectedEmployee?: EmployeeInterface;
    isEditEmployeeModalOpen: boolean;
    isPatchEditEmployeeSuccess: boolean;
    formValues?: {
        firstName: string;
        lastName: string;
        insertion?: string;
        functionGroups: { selectedId: string; selectedValue: string }[];
        businessEmail: string;
        privateEmail: string;
        newEmail: string;
        telephoneNumber: string;
        dateOfBirth: string;
        placeOfBirth: string;
    };
    error?: string;
    functionGroupInputValue?: string;
    // Email edit state
    newEmailAddress: string;
    confirmEmailAddress: string;
    emailError: string | null;
    newEmailError: boolean;
    confirmEmailError: boolean;
    newEmailSuccess: boolean;
    confirmEmailSuccess: boolean;
    showEmailEdit: boolean;
    resetEmailState?: {
        newEmailAddress: string;
        confirmEmailAddress: string;
        emailError: null;
        newEmailError: boolean;
        confirmEmailError: boolean;
        newEmailSuccess: boolean;
        confirmEmailSuccess: boolean;
        showEmailEdit: boolean;
    };
}

const initialState: EditEmployeeModalInterface = {
    isEditEmployeeModalOpen: false,
    isPatchEditEmployeeSuccess: false,
    // Email edit state
    newEmailAddress: "",
    confirmEmailAddress: "",
    emailError: null,
    newEmailError: false,
    confirmEmailError: false,
    newEmailSuccess: false,
    confirmEmailSuccess: false,
    showEmailEdit: false,
    resetEmailState: undefined,
};

const editEmployeeModalSlice = createSlice({
    name: "editEmployee",
    initialState,
    reducers: {
        setIsEditEmployeeModalOpen: (state, action) => {
            state.isEditEmployeeModalOpen = action.payload;
        },
        setIsPatchEditEmployeeSuccess: (state, action) => {
            state.isPatchEditEmployeeSuccess = action.payload;
        },
        setFormValues: (state, action) => {
            state.formValues = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        setFunctionGroupInputValue: (state, action) => {
            state.functionGroupInputValue = action.payload;
        },
        setSelectedEmployee: (state, action) => {
            state.selectedEmployee = action.payload;
        },
        // Email edit reducers
        setNewEmailAddress: (state, action) => {
            state.newEmailAddress = action.payload;
        },
        setConfirmEmailAddress: (state, action) => {
            state.confirmEmailAddress = action.payload;
        },
        setEmailError: (state, action) => {
            state.emailError = action.payload;
        },
        setNewEmailError: (state, action) => {
            state.newEmailError = action.payload;
        },
        setConfirmEmailError: (state, action) => {
            state.confirmEmailError = action.payload;
        },
        setNewEmailSuccess: (state, action) => {
            state.newEmailSuccess = action.payload;
        },
        setConfirmEmailSuccess: (state, action) => {
            state.confirmEmailSuccess = action.payload;
        },
        setShowEmailEdit: (state, action) => {
            state.showEmailEdit = action.payload;
        },
        setResetEmailState: (state, action) => {
            state.resetEmailState = action.payload;
            // Also reset the actual email fields
            if (action.payload) {
                state.newEmailAddress = action.payload.newEmailAddress;
                state.confirmEmailAddress = action.payload.confirmEmailAddress;
                state.emailError = action.payload.emailError;
                state.newEmailError = action.payload.newEmailError;
                state.confirmEmailError = action.payload.confirmEmailError;
                state.newEmailSuccess = action.payload.newEmailSuccess;
                state.confirmEmailSuccess = action.payload.confirmEmailSuccess;
                state.showEmailEdit = action.payload.showEmailEdit;
            }
        },
    },
});

export const editEmployeeModalReducer = editEmployeeModalSlice.reducer;

export const {
    setError,
    setFunctionGroupInputValue,
    setFormValues,
    setIsEditEmployeeModalOpen,
    setSelectedEmployee,
    setIsPatchEditEmployeeSuccess,
    // Email edit actions
    setNewEmailAddress,
    setConfirmEmailAddress,
    setEmailError,
    setNewEmailError,
    setConfirmEmailError,
    setNewEmailSuccess,
    setConfirmEmailSuccess,
    setShowEmailEdit,
    setResetEmailState,
} = editEmployeeModalSlice.actions;
